import type { Point } from 'slate'

import { findCharacterLimitOffset } from '@buffer-mono/char-toolkit'

import {
  getPlainText,
  getNodePlainText,
} from '~publish/legacy/editor/BufferEditor'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

type findCharacterLimitPoint = (
  /**
   * A Plate editor instance
   */
  editor: BufferEditor,

  /**
   * The total amount of allowed characters in the editor
   */
  characterLimit: number | null,

  /**
   * A callback to calculate the current character count of
   * a given text string.
   * Defaults to (text) => text.length
   */
  getTextCharacterCount?: (text: string) => number,

  /**
   * An optional callback to calculate any additional characters
   * to be added, for example, if attachments change the character count
   */
  getAdditionalCharacterCount?: (text: string) => number,
) => Point | undefined

/**
 * Find the point in an editor's node tree where
 * the text reaches the provided character limit.
 */
export const findCharacterLimitPoint: findCharacterLimitPoint = (
  editor,
  characterLimit,
  getTextCharacterCount = (str) => str.length,
  getAdditionalCharacterCount,
): Point | undefined => {
  // Check against undefined and null instead of just NOT
  // to prevent returning early when given a characterLimit of 0

  // TODO: Rename function to something more suitable to finding
  // a point in the node tree at a given index, instead of a character
  // limit as providing 0 for character limit implies the need for early return
  if (characterLimit === undefined || characterLimit === null) return

  // If there are any additional characters that need
  // to be added to the count, use that count as
  // the initial character count value
  const editorText = getPlainText(editor)
  let characterCount = getAdditionalCharacterCount?.(editorText) || 0
  let characterLimitPoint: Point | undefined

  const nodeEntries = editor.api.nodes({
    at: [],
    voids: false,
  })
  Array.from(nodeEntries).every(([node, path]) => {
    const nodeText = getNodePlainText(editor, [node, path]) || ''
    const currentNodeCount = getTextCharacterCount(nodeText)
    characterCount += currentNodeCount

    // If the current node text doesn't exceed the limit,
    // continue looping over the nodes
    const charsAboveLimit = characterCount - characterLimit
    if (charsAboveLimit <= 0) return true

    // If the current node text does exceed the limit, scan its grapheme
    // clusters with the same counting rules (getTextCharacterCount) to find
    // the exact UTF-16 offset where the limit is first exceeded. A naive
    // "node text length minus overage" subtraction mixes UTF-16 units with
    // a platform count that may not be 1:1 with them (grapheme-based
    // counters, URL/mention substitution), which can both split a
    // multi-code-unit character and land on the wrong character entirely.
    const countBeforeNode = characterCount - currentNodeCount
    const startOffset = findCharacterLimitOffset(
      nodeText,
      getTextCharacterCount,
      characterLimit - countBeforeNode,
    )

    characterLimitPoint = {
      path,
      offset: startOffset,
    }
    return false
  })

  return characterLimitPoint
}
